<script setup lang="ts">

import {apps, Models, useOrderViewContext} from "@hantera/portal-app";
import {DropdownMenu, InlineInput} from "@hantera/design-system";
import {nextTick, ref, useTemplateRef, watch} from "vue";
import ProductSearchHit from "@/components/Order/Deliveries/OrderLine/ProductSearchHit.vue";
import useApps from "@/composition/use-apps";

const props = defineProps<{
  delivery: Models.Graph.Delivery
  orderLine?: Models.Graph.OrderLine
}>()

const emit = defineEmits<{
  (event: 'confirm', value: apps.standardServices.OrderProduct): void
}>();

const orderViewContext = useOrderViewContext()

const isEditing = ref(false)
const textInput = useTemplateRef<InstanceType<typeof InlineInput>>('textInput')
const productNumber = ref<string>();
let searchDebouncer: ReturnType<typeof setTimeout> | undefined;
const searchHits = ref<apps.standardServices.OrderProduct[]>([]);
const selectedHit = ref<apps.standardServices.OrderProduct | undefined>(undefined);
const appsContext = useApps();
const searchHitsDropdown = useTemplateRef<InstanceType<typeof DropdownMenu>>('searchHitsDropdown')
const editorElem = useTemplateRef<HTMLDivElement>('editorElem')

function edit() {
  productNumber.value = props.orderLine?.productNumber
  
  nextTick(() => {
    isEditing.value = true;
    nextTick(() => {
      textInput.value?.focus();
    })
  })
}

watch(productNumber, () => {
  if (!isEditing.value) {
    return
  }
  
  clearTimeout(searchDebouncer)

  searchDebouncer = setTimeout(search, 200);
})

async function search() {
  if (!isEditing.value) {
    return;
  }
  
  if (!productNumber.value) {
    return []
  }

  const services = appsContext.getServices(apps.standardServices.productsService);

  const products: apps.standardServices.OrderProduct[] = [];

  for (const s of services) {
    products.unshift(...await s.order(
      orderViewContext.state,
      productNumber.value!
    ))
  }

  searchHits.value = products;
}

function cancel() {
  isEditing.value = false
  searchHits.value = []
}

function select(hit?: apps.standardServices.OrderProduct) {
  selectedHit.value = hit;
}

async function confirm() {
  if (!selectedHit.value) {
    isEditing.value = false
    searchHits.value = []
    return;
  }

  emit('confirm', selectedHit.value)
  
  await orderViewContext.refresh()
  isEditing.value = false
  searchHits.value = []
}

function keydown(event: KeyboardEvent) {
  
  if (!isEditing.value || searchHits.value.length === 0) {
    return;
  }
  
  if (event.key === 'ArrowDown') {
    if (!selectedHit.value) {
      selectedHit.value = searchHits.value[0];
      event.preventDefault()
      return;
    }
    
    const currentIdx = searchHits.value.findIndex(r => r.productNumber === selectedHit.value?.productNumber);
    if (currentIdx < searchHits.value.length - 1) {
      selectedHit.value = searchHits.value[currentIdx + 1]
      event.preventDefault()
    }
  }
  if (event.key === 'ArrowUp') {
    if (!selectedHit.value) {
      return;
    }

    const currentIdx = searchHits.value.findIndex(r => r.productNumber === selectedHit.value?.productNumber);
    if (currentIdx > 0) {
      selectedHit.value = searchHits.value[currentIdx - 1]
      event.preventDefault()
    }
  }
  if (event.key === 'Enter') {
    confirm();
  }
  if (event.key === 'Escape') {
    cancel();
  }
}

watch(() => searchHits.value.length, () => {
  if (!searchHitsDropdown.value) {
    return
  }

  if (searchHits.value.length > 0) {

    searchHitsDropdown.value.open()
  } else {
    searchHitsDropdown.value.close()
  }
})

</script>

<template>
  <div v-if="delivery.deliveryState === 'open'">
    <button class="btn-link" v-if="!isEditing" @click="edit">
      {{orderLine?.productNumber ?? 'Add product...'}}
    </button>
    <div v-else class="editor" ref="editorElem">
      <InlineInput ref="textInput" v-model="productNumber" @keydown.stop="keydown" />
      <button class="btn-link" @click="cancel">Cancel</button>
    </div>
    <DropdownMenu ref="searchHitsDropdown" :target="editorElem" :popover="true">
      <ProductSearchHit
        v-for="hit in searchHits"
        :key="hit.productNumber"
        :hit="hit"
        @mouseenter="select(hit)"
        @click="confirm"
        :class="{
          'search-hit': true,
          'search-hit--selected': selectedHit?.productNumber === hit.productNumber
        }"
      />
    </DropdownMenu>
  </div>
  <div v-else-if="orderLine">
    {{orderLine.productNumber}}
  </div>
</template>

<style scoped>
.editor {
  display: flex;
  align-items: center;
  gap: 5px;
  flex-wrap: nowrap;
}

.search-hits-container {
  position: relative;
}

.search-hits {
  position: absolute;
  left: 0;
  right: 0;
  z-index: 100;
  
  display: flex;
  flex-direction: column;
}

.search-hit {
  transition: background-color 100ms;
}

.search-hit--selected {
  background-color: hsl(var(--color-80));
}
</style>